import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { NotifyService } from "../services/notify.service";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

import { CredentialService } from "../credential.service";
import { NavigationService } from "../shared/navigation.service";
import { Urls } from "../shared/urls";
import { MONITORING_SUPERSEDED_ERROR_CODE } from "../enums/monitoring.enum";
import { REVIEW_WORKFLOW_ERROR_KEYS } from "../services/tasks/review-workflow-error.const";
import { USER_DELETE_HANDLED_CODES } from "../services/internal-users/user-delete-error.const";
import { AssignedTasksFacadeService } from "../services/assigned-tasks-facade.service";

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    public userService: CredentialService,
    public router: Router,
    public navigationService: NavigationService,
    private notificationService: NotifyService,
    readonly translateService: TranslateService,
    private readonly assignedTasksFacade: AssignedTasksFacadeService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check and store the custom header before processing, then strip it before sending to API
    const skipLogoutOn401 = request.headers.has("X-Skip-Logout-On-401");
    if (skipLogoutOn401) {
      request = request.clone({
        headers: request.headers.delete("X-Skip-Logout-On-401")
      });
    }

    if (
      this.userService.getToken() &&
      !request.url.includes("https://flowtribe-translation.s3.eu-central-1.amazonaws.com") &&
      !request.headers.has("authorization")
    ) {
      request = request.clone({
        setHeaders: {
          authorization: `${this.userService.getToken()}`
        }
      });
    }
    return next.handle(request).pipe(
      tap(
        (res: any) => {
          // check firm on boarding that time redirect to the welcome page
          if (res instanceof HttpResponse) {
            if (res.body && res.body.result && res.body.result.hasOwnProperty("isFirmPolicyComplete")) {
              if (!res.body.result.isFirmPolicyComplete) {
                this.navigationService.removeSidebar();
                this.navigationService.policyData = res.body.result;
                this.router.navigate(["welcome"]);
              }
            }
          }
        },
        (err: any) => {
          if (err instanceof HttpErrorResponse) {
            const alertMessages = [
              "ALREADY_ADDED_AS_UBO_IN_SELECTED_COMPANY",
              "ALREADY_ADDED_AS_OTHERDIRECTOR_IN_SELECTED_COMPANY",
              "ALREADY_ADDED_AS_PERMANENTREPRESENTATIVE_IN_SELECTED_COMPANY",
              "ALREADY_ADDED_AS_COMPANYDIRECTOR_IN_SELECTED_COMPANY",
              "SELECTED_PRIVATEPERSON_IS_ALREADY_ADDED_AS_UBO",
              "SELECTED_PRIVATEPERSON_IS_ALREADY_ADDED_AS_OTHERDIRECTOR",
              "SELECTED_COMPANY_IS_ALREADY_ADDED_AS_COMPANYDIRECTOR",
              "SELECTED_PRIVATEPERSON_IS_ALREADY_ADDED_AS_PERMANENTREPRESENTATIVE"
            ];

            const isExcludedUrl = request.url.startsWith(Urls.accountonIntegrationsUrl) || request.url.startsWith(Urls.integrationsUrl);

            const hasDedicatedReviewWorkflowToast =
              request.url.endsWith("/approve") ||
              request.url.endsWith("/reject") ||
              request.url.endsWith("/resolve") ||
              request.url.endsWith("/assignees") ||
              request.url.endsWith("/notify-on-complete") ||
              request.url.includes("/assign-to/") ||
              request.url.includes("/by-transcript/");
            const isHandledReviewWorkflowError =
              request.url.includes(Urls.reviewWorkflowInstancePath) &&
              hasDedicatedReviewWorkflowToast &&
              !!err.error?.code &&
              Object.prototype.hasOwnProperty.call(REVIEW_WORKFLOW_ERROR_KEYS, err.error.code);

            const isHandledUserDeletionError =
              request.method === "DELETE" &&
              /\/user\/[^/]+$/.test(request.url) &&
              !!err.error?.code &&
              USER_DELETE_HANDLED_CODES.includes(err.error.code);

            // Always reported by the caller, which says what failed; a second generic toast would only repeat it.
            const isSigningRetryCall = request.method === "POST" && request.url.endsWith("/signing/retry");

            const isMonitoringEventFetch = request.method === "GET" && new RegExp(`/${Urls.monitoringPath}/[^/]+$`).test(request.url);

            const isBenignInstanceNotFound = err.status === 404 && request.url.includes("/by-transcript/");

            const isBenignAnswersNotFound = err.status === 404 && request.url.endsWith("/answers");

            const isSilentStartCall = request.url.includes(Urls.reviewWorkflowInstancePath) && request.url.endsWith("/start");

            const isCustomerAccessRefusal =
              err.status === 403 && request.url.includes(Urls.reviewWorkflowInstancePath) && request.url.includes("/by-transcript/");

            const isScanningAdverse404 =
              err.status === 404 &&
              (request.url.includes("/adverse-media/customer/") || request.url.includes("/sanctions-and-political-involvement/customer/"));

            // The review panel toasts this one itself, with a link to the event that replaced this one.
            const isSupersededReview = err.error?.code === MONITORING_SUPERSEDED_ERROR_CODE;

            if (isScanningAdverse404) {
              let scanningMessage: string;
              if (request.url.includes("/adverse-media/customer/")) {
                scanningMessage = this.translateService.instant("adverse-media.Scanning_adverse_media");
              } else if (request.url.includes("/pep")) {
                scanningMessage = this.translateService.instant("political-involvements.Scanning_political_data");
              } else {
                scanningMessage = this.translateService.instant("sanctions-and-mandates.Scanning_sanctions_data");
              }
              this.notificationService.warning(scanningMessage);
            }

            const errorMessage =
              err.error && err.error.code
                ? this.translateService.instant(`APIResponseCodes.${err.error.code}`)
                : this.translateService.instant("APIResponseCodes.INTERNAL-SERVER-ERROR");
            if (
              !isScanningAdverse404 &&
              !isSupersededReview &&
              !isHandledReviewWorkflowError &&
              !isHandledUserDeletionError &&
              !isBenignInstanceNotFound &&
              !isBenignAnswersNotFound &&
              !isSilentStartCall &&
              !isSigningRetryCall &&
              !isMonitoringEventFetch &&
              !isCustomerAccessRefusal &&
              err.error &&
              err.error.code !== "AUTH-FAIL" &&
              err.error.code !== "TOKEN-EXPIRED" &&
              err.error.entity !== "RR_NUMBER" &&
              err.error.entity !== "KBO_NUMBER" &&
              err.error.error !== "CUSTOMER_ARCHIVED" &&
              err.error.error !== "CANNOT_DELETE_CURRENT_AML_POLICY" &&
              err.error.error !== "REVIEW_WORKFLOW_QUESTIONNAIRE_ASSIGNEES_ONLY" &&
              err.error.error !== "CUSTOMER_ACCESS_DENIED" &&
              err.error.code !== "REVIEW_WORKFLOW_DOCUMENT_COLLECTS_SIGNATURES" &&
              err.error.message !== "Verification_task_cannot_be_added" &&
              err.error.message !== "Verification_task_already_added" &&
              err.error.message !== "Private_person_IDRenewal_task_cannot_be_added" &&
              err.error.message !== "NOT_ALLOWED_TO_ADD_SAME_COMPANY_AS_DIRECTOR" &&
              !alertMessages.includes(err.error.message) &&
              !(err.status === 401 && !isExcludedUrl && !skipLogoutOn401)
            ) {
              this.notificationService.error(errorMessage);
            }
            if (err.status === 401 && !isExcludedUrl && !skipLogoutOn401) {
              if (!this.userService.getToken()) {
                this.assignedTasksFacade.stopPolling();
                localStorage.clear();
                this.router.navigate(["/login"]);
                return;
              }

              // this will trigger a full app reload
              this.userService.logoutAndInvalidateToken();
            }
          }
        }
      )
    );
  }
}
